import { useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Form, LoadingSpinner } from '../../../../../components';
import {
  CheckboxGroup,
  ExamCenterSatisfactionRow,
  FormSearchSelection,
  RadioGroup,
} from '../../components';

import { EXAM_PROVINCE_CODES } from './constants';
import {
  type QuestionnaireLongFormInput,
  type QuestionnaireLongFormOutput,
  QuestionnaireLongFormSchema,
} from './questionnaire-long-form-schema';

const formLabelClassName = 'flex gap-2 text-base font-semibold';
const labelSpanText = 'after:text-danger after:ml-1 after:content-["*"]';

interface QuestionnaireLongFormProps extends Omit<React.ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: QuestionnaireLongFormOutput) => Promise<void>;
}

export default function QuestionnaireLongForm(props: Readonly<QuestionnaireLongFormProps>) {
  const { className, onSubmit, ...restProps } = props;
  const { t, i18n } = useTranslation();

  const formContext = useForm<QuestionnaireLongFormInput, undefined, QuestionnaireLongFormOutput>({
    defaultValues: {
      age: '',
      highestEducationLevel: { value: '', other: '' },
      major: { value: '', other: '' },
      examProvince: '',
      currentOccupation: { value: '', other: '' },
      currentPosition: '',
      currentOrganization: { value: '', other: '' },
      currentOrganizationName: '',
      workExperience: '',
      examAttempted: { value: '', amount: '' },
      examPreparations: { value: [], other: '' },
      examAttendingObjectives: { value: '', other: '' },
      examDevice: { value: '', other: '' },
      referralSource: { value: '', other: '' },
      examReadiness: '',
      examinationSatisfaction: {
        location: '',
        staffServices: '',
        systemUsability: '',
        systemStability: '',
      },
      comment: '',
    },
    resolver: zodResolver(QuestionnaireLongFormSchema),
  });

  // Clear the free-text "other" input whenever the paired radio moves off "other".
  useEffect(() => {
    const subscription = formContext.watch((value, { name, type }) => {
      if (type !== 'change') return;
      const fields = [
        'highestEducationLevel',
        'major',
        'currentOccupation',
        'currentOrganization',
        'examAttendingObjectives',
        'examDevice',
        'referralSource',
      ] as const;
      for (const field of fields) {
        if (name === `${field}.value` && value[field]?.value !== 'other') {
          if (value[field]?.other) formContext.setValue(`${field}.other`, '');
          formContext.clearErrors(`${field}.other`);
        }
      }

      if (name === 'examAttempted.value' && value.examAttempted?.value !== 'more-than-zero') {
        if (value.examAttempted?.amount) formContext.setValue('examAttempted.amount', '');
        formContext.clearErrors('examAttempted.amount');
      }

      if (name === 'examPreparations.value' && !value.examPreparations?.value?.includes('other')) {
        if (value.examPreparations?.other) formContext.setValue('examPreparations.other', '');
        formContext.clearErrors('examPreparations.other');
      }
    });
    return () => subscription.unsubscribe();
  }, [formContext]);

  const highestEducationLevelOptions = [
    {
      label: t('questionnaire.highestEducationLevel.options.belowBachelorDegree'),
      value: 'below-bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.currentlyInBachelorDegree'),
      value: 'currently-in-bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.bachelorDegree'),
      value: 'bachelor-degree',
    },
    {
      label: t('questionnaire.highestEducationLevel.options.masterDegreeOrHigher'),
      value: 'master-degree-or-higher',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='highestEducationLevel.other'
            className='h-10 placeholder:font-light'
            disabled={!(formContext.watch('highestEducationLevel.value') === 'other')}
            placeholder={t('questionnaire.highestEducationLevel.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const majorOptions = [
    { label: t('questionnaire.major.option.financial'), value: 'financial' },
    { label: t('questionnaire.major.option.accounting'), value: 'accounting' },
    { label: t('questionnaire.major.option.mathematics'), value: 'mathematics' },
    { label: t('questionnaire.major.option.economics'), value: 'economics' },
    {
      label: t('questionnaire.major.option.business-administration'),
      value: 'business-administration',
    },
    { label: t('questionnaire.major.option.computer-science'), value: 'computer-science' },
    { label: t('questionnaire.major.option.engineering'), value: 'engineering' },
    { label: t('questionnaire.major.option.natural-science'), value: 'natural-science' },
    { label: t('questionnaire.major.option.health-science'), value: 'health-science' },
    { label: t('questionnaire.major.option.social-science'), value: 'social-science' },
    { label: t('questionnaire.major.option.other'), value: 'other' },
  ];
  const shouldShowMajorOther = formContext.watch('major.value') === 'other';
  const examProvinceOptions = EXAM_PROVINCE_CODES.map((code) => ({
    label: t(`questionnaire.examProvince.option.${code}`),
    value: code,
  })).sort((a, b) => a.label.localeCompare(b.label, i18n.language));

  const shouldDisableCurrentOccupationOther = !(
    formContext.watch('currentOccupation.value') === 'other'
  );

  const currentOccupationOptions = [
    { label: t('questionnaire.currentOccupation.option.student'), value: 'student' },
    {
      label: t('questionnaire.currentOccupation.option.company-employee'),
      value: 'company-employee',
    },
    {
      label: t('questionnaire.currentOccupation.option.business-owner'),
      value: 'business-owner',
    },
    { label: t('questionnaire.currentOccupation.option.freelance'), value: 'freelance' },
    { label: t('questionnaire.currentOccupation.option.government'), value: 'government' },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='currentOccupation.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableCurrentOccupationOther}
            placeholder={t('questionnaire.currentOccupation.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableCurrentOrganizationOther = !(
    formContext.watch('currentOrganization.value') === 'other'
  );

  const currentOrganizationOptions = [
    { label: t('questionnaire.currentOrganization.option.studying'), value: 'studying' },
    {
      label: t('questionnaire.currentOrganization.option.securities-company'),
      value: 'securities-company',
    },
    {
      label: t('questionnaire.currentOrganization.option.asset-management-company'),
      value: 'asset-management-company',
    },
    { label: t('questionnaire.currentOrganization.option.bank'), value: 'bank' },
    {
      label: t('questionnaire.currentOrganization.option.insurance-company'),
      value: 'insurance-company',
    },
    { label: t('questionnaire.currentOrganization.option.government'), value: 'government' },
    {
      label: t('questionnaire.currentOrganization.option.private-organization'),
      value: 'private-organization',
    },
    { label: t('questionnaire.currentOrganization.option.freelance'), value: 'freelance' },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='currentOrganization.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableCurrentOrganizationOther}
            placeholder={t('questionnaire.currentOrganization.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const workExperienceOptions = [
    { label: t('questionnaire.workExperience.option.none'), value: 'none' },
    {
      label: t('questionnaire.workExperience.option.less-than-1-year'),
      value: 'less-than-1-year',
    },
    {
      label: t('questionnaire.workExperience.option.1-to-3-years'),
      value: '1-to-3-years',
    },
    {
      label: t('questionnaire.workExperience.option.4-to-7-years'),
      value: '4-to-7-years',
    },
    {
      label: t('questionnaire.workExperience.option.more-than-7-years'),
      value: 'more-than-7-years',
    },
  ];

  const shouldDisableExamAttemptedAmount = !(
    formContext.watch('examAttempted.value') === 'more-than-zero'
  );

  const examAttemptedOptions = [
    { label: t('questionnaire.previouslyAttemptedExam.option.never'), value: 'never' },
    {
      label: t('questionnaire.previouslyAttemptedExam.option.moreThanZero'),
      value: 'more-than-zero',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='examAttempted.amount'
            type='number'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableExamAttemptedAmount}
            placeholder={t(
              'questionnaire.previouslyAttemptedExam.option.moreThanZero.inputPlaceholder',
            )}
          />
        </div>
      ),
    },
  ];

  const shouldDisableExamPreparationsOther = !formContext
    .watch('examPreparations.value')
    .includes('other');

  const examPreparationOptions = [
    {
      label: t('questionnaire.examPreparations.option.prepare-exam-by-self'),
      value: 'prepare-exam-by-self',
    },
    {
      label: t('questionnaire.examPreparations.option.attend-training-from-official'),
      value: 'attend-training-from-official',
    },
    {
      label: t('questionnaire.examPreparations.option.attend-training-from-training-institution'),
      value: 'attend-training-from-training-institution',
    },
    {
      label: t('questionnaire.examPreparations.option.attend-specific-program', {
        examples: 'New Breed, TFEX Academy, ESG Academy',
      }),
      value: 'attend-specific-program',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='examPreparations.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableExamPreparationsOther}
            placeholder={t('questionnaire.examPreparations.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableExamAttendingObjectivesOther = !(
    formContext.watch('examAttendingObjectives.value') === 'other'
  );

  const examAttendingObjectivesOptions = [
    {
      label: t('questionnaire.examAttendingObjectives.option.career-opportunity'),
      value: 'career-opportunity',
    },
    {
      label: t('questionnaire.examAttendingObjectives.option.register-license-and-profession'),
      value: 'register-license-and-profession',
    },
    {
      label: t('questionnaire.examAttendingObjectives.option.self-assessment'),
      value: 'self-assessment',
    },
    {
      label: t(
        'questionnaire.examAttendingObjectives.option.special-project-with-outside-institution',
      ),
      value: 'special-project-with-outside-institution',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='examAttendingObjectives.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableExamAttendingObjectivesOther}
            placeholder={t('questionnaire.examAttendingObjectives.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableExamDeviceOther = !(formContext.watch('examDevice.value') === 'other');

  const examDeviceOptions = [
    { label: t('questionnaire.examDevice.option.laptop'), value: 'laptop' },
    { label: t('questionnaire.examDevice.option.pc'), value: 'pc' },
    { label: t('questionnaire.examDevice.option.tablet-ipad'), value: 'tablet-ipad' },
    { label: t('questionnaire.examDevice.option.tablet-android'), value: 'tablet-android' },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='examDevice.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableExamDeviceOther}
            placeholder={t('questionnaire.examDevice.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const shouldDisableReferralSourceOther = !(formContext.watch('referralSource.value') === 'other');

  const referralSourceOptions = [
    { label: t('questionnaire.referralSource.option.social-media'), value: 'social-media' },
    {
      label: t('questionnaire.referralSource.option.friend-recommendation'),
      value: 'friend-recommendation',
    },
    {
      label: t('questionnaire.referralSource.option.organization-or-institution'),
      value: 'organization-or-institution',
    },
    { label: t('questionnaire.referralSource.option.email-pr'), value: 'email-pr' },
    {
      label: t('questionnaire.referralSource.option.website-announcement'),
      value: 'website-announcement',
    },
    {
      label: t('questionnaire.option.other'),
      value: 'other',
      endAdornment: (
        <div className='w-95'>
          <Form.Input
            name='referralSource.other'
            className='h-10 placeholder:font-light'
            disabled={shouldDisableReferralSourceOther}
            placeholder={t('questionnaire.referralSource.inputPlaceholder')}
          />
        </div>
      ),
    },
  ];

  const examReadinessOptions = [
    { label: t('questionnaire.examReadiness.option.most'), value: 'most' },
    { label: t('questionnaire.examReadiness.option.high'), value: 'high' },
    { label: t('questionnaire.examReadiness.option.low'), value: 'low' },
    { label: t('questionnaire.examReadiness.option.least'), value: 'least' },
  ];

  const satisfactionColumns = [
    { label: t('questionnaire.examinationSatisfaction.option.very-good'), value: 'very-good' },
    { label: t('questionnaire.examinationSatisfaction.option.good'), value: 'good' },
    { label: t('questionnaire.examinationSatisfaction.option.fair'), value: 'fair' },
    {
      label: t('questionnaire.examinationSatisfaction.option.need-improvement'),
      value: 'need-improvement',
    },
  ];

  const satisfactionRows = [
    {
      field: 'location',
      label: t('questionnaire.examinationSatisfaction.criteria.location'),
    },
    {
      field: 'staffServices',
      label: t('questionnaire.examinationSatisfaction.criteria.staff-services'),
    },
  ] as const;

  const satisfactionSystemRows = [
    {
      field: 'systemUsability',
      label: t('questionnaire.examinationSatisfaction.criteria.system-usability'),
    },
    {
      field: 'systemStability',
      label: t('questionnaire.examinationSatisfaction.criteria.system-stability'),
    },
  ] as const;

  const satisfactionGridClassName =
    'grid w-full grid-cols-[1fr_repeat(4,minmax(80px,140px))] items-center';
  const satisfactionDividerClassName = 'w-full border-neutral-300 border-t';

  const handleSubmit = async (data: QuestionnaireLongFormOutput) => {
    await onSubmit(data);
  };

  return (
    <FormProvider {...formContext}>
      <form
        className={cn('flex flex-col gap-6', className)}
        onSubmit={formContext.handleSubmit(handleSubmit)}
        {...restProps}
      >
        <div className='grid gap-2 placeholder:font-light'>
          <label htmlFor='age' className={formLabelClassName}>
            <span>1.</span>
            <span className={labelSpanText}>{t('questionnaire.age.questionTitle')}</span>
          </label>
          <div className='ml-5.5 max-w-95'>
            <Form.Input
              name='age'
              placeholder={t('questionnaire.age.inputPlaceholder')}
              type='number'
              className='h-10'
            />
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='highestEducationLevel' className={formLabelClassName}>
            <span>2.</span>
            <span className={labelSpanText}>
              {t('questionnaire.highestEducationLevel.questionTitle')}
            </span>
          </label>
          <RadioGroup
            name='highestEducationLevel.value'
            className='ml-3.5'
            options={highestEducationLevelOptions}
          />
        </div>

        <div className='grid gap-2'>
          <label htmlFor='major' className={formLabelClassName}>
            <span>3.</span>
            <span className={labelSpanText}>{t('questionnaire.major.title')}</span>
          </label>
          <div className='ml-5.5 flex flex-wrap items-start gap-4'>
            <div className='w-95'>
              <FormSearchSelection
                name='major.value'
                options={majorOptions}
                placeholder={t('questionnaire.major.inputPlaceholder')}
              />
            </div>
            {shouldShowMajorOther ? (
              <div className='w-95'>
                <Form.Input
                  name='major.other'
                  className='h-11'
                  placeholder={t('questionnaire.major.otherInputPlaceholder')}
                />
              </div>
            ) : null}
          </div>
        </div>

        <div className='grid gap-2'>
          <label htmlFor='examProvince' className={formLabelClassName}>
            <span>4.</span>
            <span className={labelSpanText}>{t('questionnaire.examProvince.title')}</span>
          </label>
          <div className='ml-5.5 max-w-95'>
            <FormSearchSelection
              name='examProvince'
              options={examProvinceOptions}
              placeholder={t('questionnaire.examProvince.inputPlaceholder')}
            />
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='currentOccupation' className={formLabelClassName}>
            <span>5.</span>
            <span className={labelSpanText}>{t('questionnaire.currentOccupation.title')}</span>
          </label>
          <RadioGroup
            name='currentOccupation.value'
            className='ml-3.5'
            options={currentOccupationOptions}
          />
        </div>

        <div className='grid gap-2 placeholder:font-light'>
          <label htmlFor='currentPosition' className={formLabelClassName}>
            <span>6.</span>
            <span>{t('questionnaire.currentPosition.title')}</span>
          </label>
          <div className='ml-5.5 max-w-95'>
            <Form.Input
              name='currentPosition'
              placeholder={t('questionnaire.currentPosition.inputPlaceholder')}
              className='h-10'
            />
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='currentOrganization' className={formLabelClassName}>
            <span>7.</span>
            <span className={labelSpanText}>{t('questionnaire.currentOrganization.title')}</span>
          </label>
          <RadioGroup
            name='currentOrganization.value'
            className='ml-3.5'
            options={currentOrganizationOptions}
          />
        </div>

        <div className='grid gap-2 placeholder:font-light'>
          <label htmlFor='currentOrganizationName' className={formLabelClassName}>
            <span>8.</span>
            <span>{t('questionnaire.currentOrganizationName.title')}</span>
          </label>
          <div className='ml-5.5 max-w-95'>
            <Form.Input
              name='currentOrganizationName'
              placeholder={t('questionnaire.currentOrganizationName.inputPlaceholder')}
              className='h-10'
            />
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='workExperience' className={formLabelClassName}>
            <span>9.</span>
            <span className={labelSpanText}>{t('questionnaire.workExperience.title')}</span>
          </label>
          <RadioGroup name='workExperience' className='ml-3.5' options={workExperienceOptions} />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examAttempted' className={formLabelClassName}>
            <span>10.</span>
            <span className={labelSpanText}>
              {t('questionnaire.previouslyAttemptedExam.title')}
            </span>
          </label>
          <RadioGroup
            name='examAttempted.value'
            className='ml-3.5'
            options={examAttemptedOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examPreparations' className={formLabelClassName}>
            <span>11.</span>
            <span className={labelSpanText}>{t('questionnaire.examPreparations.title')}</span>
          </label>
          <CheckboxGroup
            name='examPreparations.value'
            className='ml-5.5'
            options={examPreparationOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examAttendingObjectives' className={formLabelClassName}>
            <span>12.</span>
            <span className={labelSpanText}>
              {t('questionnaire.examAttendingObjectives.title')}
            </span>
          </label>
          <RadioGroup
            name='examAttendingObjectives.value'
            className='ml-3.5'
            options={examAttendingObjectivesOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examDevice' className={formLabelClassName}>
            <span>13.</span>
            <span className={labelSpanText}>{t('questionnaire.examDevice.title')}</span>
          </label>
          <RadioGroup name='examDevice.value' className='ml-3.5' options={examDeviceOptions} />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='referralSource' className={formLabelClassName}>
            <span>14.</span>
            <span className={labelSpanText}>{t('questionnaire.referralSource.title')}</span>
          </label>
          <RadioGroup
            name='referralSource.value'
            className='ml-3.5'
            options={referralSourceOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examReadiness' className={formLabelClassName}>
            <span>15.</span>
            <span className={labelSpanText}>{t('questionnaire.examReadiness.title')}</span>
          </label>
          <RadioGroup
            name='examReadiness'
            className='ml-3.5 flex-row flex-wrap gap-x-6'
            options={examReadinessOptions}
          />
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='examinationSatisfaction' className={formLabelClassName}>
            <span>16.</span>
            <span className={labelSpanText}>
              {t('questionnaire.examinationSatisfaction.title')}
            </span>
          </label>
          <div className='ml-5.5 flex flex-col'>
            <div className={cn(satisfactionGridClassName, 'pb-3')}>
              <div />
              {satisfactionColumns.map((column) => (
                <p key={column.value} className='text-center font-semibold'>
                  {column.label}
                </p>
              ))}
            </div>
            <div className={satisfactionDividerClassName} />
            <ExamCenterSatisfactionRow
              field={satisfactionRows[0].field}
              label={satisfactionRows[0].label}
              columns={satisfactionColumns}
              gridClassName={satisfactionGridClassName}
            />
            <div className={satisfactionDividerClassName} />
            <ExamCenterSatisfactionRow
              field={satisfactionRows[1].field}
              label={satisfactionRows[1].label}
              columns={satisfactionColumns}
              gridClassName={satisfactionGridClassName}
            />
            <div className={satisfactionDividerClassName} />
            <p className='py-2 font-semibold'>
              {t('questionnaire.examinationSatisfaction.systemGroup')}
            </p>
            {satisfactionSystemRows.map((row) => (
              <ExamCenterSatisfactionRow
                key={row.field}
                field={row.field}
                label={row.label}
                columns={satisfactionColumns}
                gridClassName={satisfactionGridClassName}
              />
            ))}
            <div className={satisfactionDividerClassName} />
            {formContext.formState.errors.examinationSatisfaction ? (
              <p className='text-danger mt-2 text-sm'>
                {t('questionnaire.examinationSatisfaction.requiredError')}
              </p>
            ) : null}
          </div>
        </div>

        <div className='flex flex-col items-start gap-2'>
          <label htmlFor='comment' className={formLabelClassName}>
            <span>17.</span>
            <span>{t('questionnaire.comment.title')}</span>
          </label>
          <div className='w-full'>
            <Form.Textarea
              name='comment'
              placeholder={t('questionnaire.comment.inputPlaceholder')}
              className='w-full'
            />
          </div>
        </div>

        <Button
          type='submit'
          className='mx-auto flex gap-2'
          disabled={formContext.formState.isSubmitting}
        >
          {formContext.formState.isSubmitting ? <LoadingSpinner /> : null}
          {t('questionnaire.submit')}
        </Button>
      </form>
    </FormProvider>
  );
}
